// Navbar.jsx - WEBSOCKET VERSION: Usa useAlarmSubscription en lugar de useAlarmMonitoring
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Button,
  Badge,
  Tooltip,
  useTheme,
  useMediaQuery,
  Chip,
  Divider
} from '@mui/material';
import {
  Menu as MenuIcon,
  MenuOpen as MenuOpenIcon,
  Notifications as NotificationsIcon,
  AssignmentTurnedIn as TaskIcon,
  AccountCircle,
  Logout,
  Settings,
  Person,
  Warning,
  CheckCircle,
  Error as ErrorIcon,
  VolumeOff as MuteIcon,
  VolumeUp as UnmuteIcon,
  LiveHelp as TutorialIcon,
  Translate as TranslateIcon,
  ShoppingCart as ShoppingCartIcon,
  Forum as ForumIcon,
} from '@mui/icons-material';
import websocketService from '../../services/websocketService';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../hooks/useAuth';
import { useLanguage } from '../../hooks/useLanguage';
import { api } from '../../services/api';
// Una sola definición del nombre del evento, importada donde se escucha: el
// patrón de duplicar la cadena en los dos archivos acaba divergiendo.
import { PORTAL_NOTIFICATIONS_READ_EVENT } from '../../pages/PortalNotificationsPage';

import { useAlarmSubscription } from '../../hooks/useWebSocket';
import ArsenalLogo from '../common/ArsenalLogo';
import { useTutorialContext } from '../../contexts/TutorialContext';
import { useCart } from '../../hooks/useCart';


const Navbar = ({ onSidebarToggle, sidebarOpen, sidebarCollapsed, height, activeAlarms = [] }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { user, logout, currentCompany } = useAuth();
  const { cartTotals, openCartDrawer } = useCart();
  const { currentLanguage, applyLanguageAndReload, getAvailableLanguages, getLanguageMeta, isChangingLanguage } = useLanguage();


  // Cambio: activeAlarms → alarms, alarmCount → activeAlarmCount
  const { alarms: wsAlarms, activeAlarmCount: alarmCount } = useAlarmSubscription();

  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationsAnchor, setNotificationsAnchor] = useState(null);
  const [languageAnchor, setLanguageAnchor] = useState(null);

  // ---- Chat: total de mensajes no leídos (badge en navbar) ----
  const [chatUnread, setChatUnread] = useState(0);
  // Tareas pendientes del usuario (indicador del navbar).
  const [pendingTasks, setPendingTasks] = useState({ total: 0, overdue: 0, due_today: 0, tasks: [] });
  const [tasksAnchor, setTasksAnchor] = useState(null);
  useEffect(() => {
    let mounted = true;
    const refresh = () => {
      api.chat.unread()
        .then((r) => {
          if (!mounted) return;
          const vals = Object.values(r.data || {});
          const total = vals.reduce((acc, v) => acc + (typeof v === 'number' ? v : (v?.count || 0)), 0);
          setChatUnread(total);
        })
        .catch(() => {});
    };
    refresh();
    const offMsg = websocketService.on('chat_message', refresh);
    const offRead = websocketService.on('chat_unread_updated', refresh);
    const offDel = websocketService.on('chat_channel_deleted', refresh);
    return () => { mounted = false; offMsg?.(); offRead?.(); offDel?.(); };
  }, []);

  // Pendientes del usuario. Se refresca al montar y cuando el backend avisa de
  // cambios en tareas; no hace polling.
  useEffect(() => {
    let mounted = true;
    const refresh = () => {
      api.tasks.myPending(8)
        .then((r) => { if (mounted) setPendingTasks(r.data || { total: 0, overdue: 0, due_today: 0, tasks: [] }); })
        .catch(() => {});
    };
    refresh();
    const offTask = websocketService.on('task_updated', refresh);
    const offCreated = websocketService.on('task_created', refresh);
    return () => { mounted = false; offTask?.(); offCreated?.(); };
  }, []);

  const availableLanguages = useMemo(() => getAvailableLanguages(), [getAvailableLanguages]);
  const activeLanguageMeta = useMemo(
    () => getLanguageMeta(user?.language || currentLanguage),
    [getLanguageMeta, user?.language, currentLanguage]
  );


  // --- Tutorial interactivo ---
  const { isCompleted, isActive, setShowDemoModal } = useTutorialContext();

  // Combine alarms from props and websocket safely
  const safeActiveAlarms = Array.isArray(activeAlarms) ? activeAlarms : [];
  const safeWsAlarms = Array.isArray(wsAlarms) ? wsAlarms : [];
  const combinedAlarms = safeActiveAlarms.length > 0 ? safeActiveAlarms : safeWsAlarms;
  const totalAlarmCount = combinedAlarms.length;

  // La campana mostraba SOLO alarmas activas, así que decía "Todo en orden"
  // mientras el buzón tenía 50 notificaciones sin leer. Son dos cosas distintas
  // y ambas tienen que verse aquí.
  const [avisosPortal, setAvisosPortal] = useState([]);
  const [sinLeerPortal, setSinLeerPortal] = useState(0);

  const cargarAvisosPortal = useCallback(() => {
    api.portalNotifications.list({ limit: 5 })
      .then((r) => setAvisosPortal(r.data?.items || []))
      .catch(() => {});
    api.portalNotifications.unreadCount()
      .then((r) => setSinLeerPortal(r.data?.unread || 0))
      .catch(() => {});
  }, []);

  useEffect(() => {
    cargarAvisosPortal();
    // Refresco de fondo por si llega algo nuevo mientras la pestaña está abierta.
    const t = setInterval(cargarAvisosPortal, 120000);
    // Leer una notificación en otra pantalla tiene que apagar el número AQUÍ.
    // Sin esto había que recargar la página para que bajara el contador.
    window.addEventListener(PORTAL_NOTIFICATIONS_READ_EVENT, cargarAvisosPortal);
    return () => {
      clearInterval(t);
      window.removeEventListener(PORTAL_NOTIFICATIONS_READ_EVENT, cargarAvisosPortal);
    };
  }, [cargarAvisosPortal, currentCompany?.company_id]);

  const totalCampana = totalAlarmCount + sinLeerPortal;
  const criticalAlarms = combinedAlarms.filter(alarm =>
    alarm && (alarm.status === 'CRITICAL' || alarm.severity === 'critical')
  );
  const warningAlarms = combinedAlarms.filter(alarm =>
    alarm && (alarm.status === 'WARNING' || alarm.severity === 'warning')
  );

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleTasksOpen = (event) => setTasksAnchor(event.currentTarget);
  const handleTasksClose = () => setTasksAnchor(null);

  const handleNotificationsOpen = (event) => {
    setNotificationsAnchor(event.currentTarget);
    // Justo el momento en que alguien mira el número: esperar al ciclo de dos
    // minutos lo dejaría desfasado precisamente cuando se está leyendo.
    cargarAvisosPortal();
  };

  const handleNotificationsClose = () => {
    setNotificationsAnchor(null);
  };

  const handleLanguageOpen = (event) => {
    setLanguageAnchor(event.currentTarget);
  };

  const handleLanguageClose = () => {
    setLanguageAnchor(null);
  };

  const handleLanguageSelect = async (languageCode) => {
    if (!languageCode || languageCode === currentLanguage) {
      handleLanguageClose();
      return;
    }

    handleLanguageClose();
    await applyLanguageAndReload(languageCode, (code) =>
      api.users.updateProfile({ language: code })
    );
  };

  const handleProfileClick = () => {
    navigate('/profile');
    handleProfileMenuClose();
  };

  const handleSettingsClick = () => {
    navigate('/settings');
    handleProfileMenuClose();
  };

  const handleLogout = () => {
    logout();
    handleProfileMenuClose();
  };

  const handleViewAllAlarms = () => {
    navigate('/alarms');
    handleNotificationsClose();
  };

  const formatAlarmMessage = (alarm) => {
    if (alarm.currentValue && (alarm.thresholdMin || alarm.thresholdMax)) {
      let thresholdText = '';
      if (alarm.thresholdMin && alarm.currentValue < alarm.thresholdMin) {
        thresholdText = `${alarm.currentValue?.toFixed(1)} < ${alarm.thresholdMin}`;
      } else if (alarm.thresholdMax && alarm.currentValue > alarm.thresholdMax) {
        thresholdText = `${alarm.currentValue?.toFixed(1)} > ${alarm.thresholdMax}`;
      }
      return `${alarm.metric || alarm.metric_type}: ${thresholdText}`;
    }
    return alarm.message || `${alarm.metric || alarm.metric_type} alert`;
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={1}
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          borderBottom: '1px solid',
          borderBottomColor: 'divider',
          height: height,
          borderRadius: 0,
        }}
      >
        <Toolbar
          sx={{
            px: { xs: 2, md: 3 },
            height: height,
            minHeight: t('hardcoded.components_common_navbar.heightpx_important', { height: height }),
          }}
        >
          {/* Menu Button */}
          <Tooltip
            title={isMobile ? t('navbar.toggleMenu') : sidebarCollapsed ? t('navbar.expandSidebar') : t('navbar.collapseSidebar')}
          >
            <IconButton
              edge="start"
              color="inherit"
              aria-label={t('hardcoded.components_common_navbar.toggle_sidebar')}
              onClick={onSidebarToggle}
              sx={{
                mr: 2,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                }
              }}
            >
              {!isMobile && sidebarCollapsed ? <MenuIcon /> : <MenuOpenIcon />}
            </IconButton>
          </Tooltip>

          {/* Logo and Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 2, overflow: 'hidden' }}>
            <Box sx={{ mr: 2, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              <ArsenalLogo sx={{ height: 25, width: 'auto' }} />
            </Box>

            <Typography
              variant="h5"
              component="div"
              sx={{
                fontWeight: 700,
                color: 'white',
                textDecoration: 'none',
                fontFamily: 'Chiq_Reduced_BlackItalic, Arial, sans-serif',
                display: { xs: 'none', sm: 'block' },
                fontSize: { sm: '1.1rem', md: '1.25rem' },
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {t('navbar.logo')}
            </Typography>
            {/* Mobile short title */}
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: 700,
                color: 'white',
                textDecoration: 'none',
                fontFamily: 'Chiq_Reduced_BlackItalic, Arial, sans-serif',
                display: { xs: 'block', sm: 'none' },
                fontSize: '1rem',
              }}
            >
              {t('navbar.logoMobile')}
            </Typography>
          </Box>

          {/* Company Context - visible for tutorial */}
          {currentCompany && (
            <Chip
              data-tutorial-id="company-context"
              label={currentCompany.company_name || currentCompany.name || t('navbar.company')}
              size="small"
              sx={{
                ml: 2,
                backgroundColor: 'rgba(255,255,255,0.15)',
                color: 'white',
                fontWeight: 700,
                fontSize: '0.72rem',
                maxWidth: { xs: 120, sm: 180 },
                // Oculto en desktop (md+): el cambio de empresa vive en el sidebar/menú.
                // Se mantiene en tablet (sm) donde aporta contexto.
                display: { xs: 'none', sm: 'flex', md: 'none' },
                '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
              }}
            />
          )}

          {/* Spacer */}
          <Box sx={{ flexGrow: 1 }} />

          {/* Right Side Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>

            {/* Tareas pendientes del usuario */}
            <Tooltip title={pendingTasks.total > 0
              ? `${pendingTasks.total} tarea(s) pendientes${pendingTasks.overdue ? ` · ${pendingTasks.overdue} vencida(s)` : ''}`
              : t('hardcoded.components_common_sidebar.tareas_aps')}>
              <IconButton
                color="inherit"
                onClick={handleTasksOpen}
                sx={{ mr: 1, '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' } }}
              >
                <Badge
                  badgeContent={pendingTasks.total}
                  color={pendingTasks.overdue > 0 ? 'error' : 'warning'}
                  sx={{ '& .MuiBadge-badge': { fontSize: '0.72rem', height: 18, minWidth: 18 } }}
                >
                  <TaskIcon />
                </Badge>
              </IconButton>
            </Tooltip>

            {/* Chat: mensajes sin leer */}
            <Tooltip title={chatUnread > 0 ? t('hardcoded.components_common_navbar.chatunread_mensajes_sin_leer', { chatUnread: chatUnread }) : t('hardcoded.components_common_sidebar.chat')}>
              <IconButton
                color="inherit"
                onClick={() => navigate('/chat')}
                sx={{ mr: 1, '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' } }}
              >
                <Badge
                  badgeContent={chatUnread}
                  color="error"
                  sx={{ '& .MuiBadge-badge': { fontSize: '0.72rem', height: 18, minWidth: 18 } }}
                >
                  <ForumIcon />
                </Badge>
              </IconButton>
            </Tooltip>

            {/* Notifications with enhanced alarm display */}
            <Tooltip title={totalCampana > 0
              ? `${totalAlarmCount} ${t('navbar.activeAlarms')} · ${sinLeerPortal} sin leer`
              : t('navbar.notifications')}>
              <IconButton
                color="inherit"
                onClick={handleNotificationsOpen}
                sx={{
                  mr: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  }
                }}
              >
                <Badge
                  badgeContent={totalCampana}
                  color={criticalAlarms.length > 0 ? "error" : "warning"}
                  sx={{
                    '& .MuiBadge-badge': {
                      fontSize: '0.75rem',
                      height: 18,
                      minWidth: 18,
                      animation: criticalAlarms.length > 0 ? 'pulse 1s infinite' : 'none',
                    }
                  }}
                >
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Tooltip>

            <Tooltip title={t('navbar.cart')}>
              <IconButton
                color="inherit"
                onClick={openCartDrawer}
                sx={{
                  mr: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  }
                }}
              >
                <Badge
                  badgeContent={cartTotals?.itemCount || 0}
                  color="error"
                  sx={{
                    '& .MuiBadge-badge': {
                      fontSize: '0.72rem',
                      height: 18,
                      minWidth: 18,
                    }
                  }}
                >
                  <ShoppingCartIcon />
                </Badge>
              </IconButton>
            </Tooltip>

            <Tooltip title={t('navbar.language')}>
              <IconButton
                color="inherit"
                onClick={handleLanguageOpen}
                disabled={isChangingLanguage}
                sx={{
                  mr: 1,
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: 1,
                  px: 0.75,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Typography component="span" sx={{ fontSize: '1rem', lineHeight: 1 }}>
                    {activeLanguageMeta.flag}
                  </Typography>
                  <TranslateIcon sx={{ fontSize: 16 }} />
                </Box>
              </IconButton>
            </Tooltip>

            {/* Botón de Tutorial Interactivo */}
            <Tooltip title={isActive ? t('demo.steps.simulationPhase') : t('demo.title')}>
              <IconButton
                onClick={() => setShowDemoModal(true)}
                color="secondary"
                disabled={false}
                sx={{
                  background: isActive
                    ? 'linear-gradient(45deg, #4CAF50, #66BB6A)'
                    : `linear-gradient(45deg, #FF6B6B, #FFA500)`,
                  color: 'white',
                  '&:hover': {
                    background: isActive
                      ? 'linear-gradient(45deg, #4CAF50, #66BB6A)'
                      : `linear-gradient(45deg, #FF5252, #FF9000)`,
                    transform: isActive ? 'none' : 'scale(1.05)'
                  },
                  animation: (!isCompleted && !isActive) ? 'pulse 2s ease-in-out infinite' : 'none',
                  '@keyframes pulse': {
                    '0%, 100%': { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(255, 107, 107, 0.7)' },
                    '50%': { transform: 'scale(1.05)', boxShadow: '0 0 0 10px rgba(255, 107, 107, 0)' }
                  }
                }}
              >
                <TutorialIcon />
              </IconButton>
            </Tooltip>
            {/* User Menu */}
            <Tooltip title={t('navbar.account')}>
              <IconButton
                edge="end"
                color="inherit"
                onClick={handleProfileMenuOpen}
                sx={{
                  ml: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  }
                }}
              >
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: theme.palette.secondary.main,
                    fontSize: '1rem',
                    fontWeight: 600,
                    border: '2px solid rgba(255, 255, 255, 0.2)',
                  }}
                >
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </Avatar>
              </IconButton>
            </Tooltip>
          </Box>

          {/* User Menu Dropdown */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 220,
                borderRadius: 2,
                boxShadow: theme.shadows[8],
                '& .MuiMenuItem-root': {
                  borderRadius: 1,
                  mx: 1,
                  my: 0.5,
                }
              },
            }}
          >
            <Box sx={{ px: 3, py: 2, borderBottom: '1px solid', borderBottomColor: 'divider' }}>
              <Typography variant="subtitle1" fontWeight={600}>
                {user?.name || ''} {user?.last_name || ''}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {user?.email || ''}
              </Typography>
            </Box>

            <Box sx={{ py: 1 }}>
              <MenuItem onClick={handleProfileClick}>
                <Person sx={{ mr: 2, fontSize: 20 }} />
                {t('navbar.profile')}
              </MenuItem>

              {(user?.type === 'admin' || user?.type === 'manager') && (
                <MenuItem onClick={handleSettingsClick}>
                  <Settings sx={{ mr: 2, fontSize: 20 }} />
                  {t('navbar.settings')}
                </MenuItem>
              )}

              <MenuItem
                onClick={handleLogout}
                sx={{
                  color: 'error.main',
                  '&:hover': {
                    backgroundColor: 'error.main',
                    color: 'white',
                  }
                }}
              >
                <Logout sx={{ mr: 2, fontSize: 20 }} />
                {t('navbar.logout')}
              </MenuItem>
            </Box>
          </Menu>

          {/* Enhanced Notifications Menu */}
          <Menu
            anchorEl={notificationsAnchor}
            open={Boolean(notificationsAnchor)}
            onClose={handleNotificationsClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 380,
                maxWidth: 450,
                maxHeight: 600,
                borderRadius: 2,
                boxShadow: theme.shadows[8],
              },
            }}
          >
            {/* Header */}
            <Box sx={{ px: 3, py: 2, borderBottom: '1px solid', borderBottomColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h6" fontWeight={600}>
                  {t('navbar.activeAlarms')}
                </Typography>
                {totalAlarmCount > 0 && (
                  <Chip
                    label={`${totalAlarmCount} ${t('common.total').toLowerCase()}`}
                    size="small"
                    color={criticalAlarms.length > 0 ? "error" : "warning"}
                    variant="filled"
                  />
                )}
              </Box>

              {/* Summary */}
              {totalAlarmCount > 0 && (
                <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                  {criticalAlarms.length > 0 && (
                    <Typography variant="body2" color="error.main" fontWeight={600}>
                      🚨 {criticalAlarms.length} {t('navbar.critical')}
                    </Typography>
                  )}
                  {warningAlarms.length > 0 && (
                    <Typography variant="body2" color="warning.main" fontWeight={600}>
                      ⚠️ {warningAlarms.length} {t('navbar.warnings')}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>

            {/* Alarm List */}
            {totalAlarmCount === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <CheckCircle
                  color="success"
                  sx={{ fontSize: 48, mb: 2, opacity: 0.7 }}
                />
                <Typography variant="h6" fontWeight={500} sx={{ mb: 1 }}>
                  {t('navbar.allInOrder')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('navbar.noActiveAlarms')}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                {combinedAlarms.slice(0, 8).map((alarm, index) => (
                  <MenuItem
                    key={alarm.key || alarm.id || index}
                    sx={{
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      borderBottom: index < combinedAlarms.length - 1 ? '1px solid' : 'none',
                      borderBottomColor: 'divider',
                      py: 2,
                      px: 3,
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 1 }}>
                      {(alarm.status === 'CRITICAL' || alarm.severity === 'critical') ? (
                        <ErrorIcon color="error" sx={{ mr: 1.5, fontSize: 18 }} />
                      ) : (
                        <Warning color="warning" sx={{ mr: 1.5, fontSize: 18 }} />
                      )}
                      <Typography variant="subtitle2" fontWeight={600}>
                        {alarm.vehicleName || alarm.vehicle_name || `${t('common.user')} ${alarm.vehicleId || alarm.vehicle_id}`}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                        {t('alarms.list.position')} {alarm.position || alarm.axle}
                      </Typography>
                    </Box>

                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      {formatAlarmMessage(alarm)}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(alarm.timestamp || alarm.last_updated || alarm.triggered_at).toLocaleString('es-ES', {
                          hour: '2-digit',
                          minute: '2-digit',
                          day: '2-digit',
                          month: '2-digit'
                        })}
                      </Typography>

                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {alarm.acknowledged && (
                          <Chip label={t('navbar.confirmed')} size="small" color="success" variant="outlined" />
                        )}
                        {alarm.emailSent && (
                          <Chip label={t('navbar.emailSent')} size="small" color="info" variant="outlined" />
                        )}
                      </Box>
                    </Box>
                  </MenuItem>
                ))}

                {/* View All Button */}
                {totalAlarmCount > 8 && (
                  <Box>
                    <Divider />
                    <MenuItem
                      onClick={handleViewAllAlarms}
                      sx={{
                        justifyContent: 'center',
                        color: 'primary.main',
                        fontWeight: 600,
                        py: 2,
                        '&:hover': {
                          backgroundColor: 'primary.main',
                          color: 'white',
                        }
                      }}
                    >
                      {t('navbar.viewAllAlarms')} ({totalAlarmCount})
                    </MenuItem>
                  </Box>
                )}
              </Box>
            )}

            {/* Notificaciones del portal: reportes entregados al usuario. Van
                aquí porque el usuario abre la campana esperando ver TODO lo que
                le llegó, no solo las alarmas activas. */}
            {(sinLeerPortal > 0 || avisosPortal.length > 0) && (
              <Box sx={{ borderTop: '1px solid', borderTopColor: 'divider' }}>
                <Box sx={{ px: 3, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Mis notificaciones
                  </Typography>
                  {sinLeerPortal > 0 && (
                    <Chip size="small" color="error" label={`${sinLeerPortal} sin leer`} />
                  )}
                </Box>
                {avisosPortal.slice(0, 4).map((n) => (
                  <MenuItem
                    key={n.id}
                    onClick={() => { handleNotificationsClose(); navigate(`/notifications?open=${n.id}`); }}
                    sx={{ px: 3, py: 1.25, display: 'block' }}
                  >
                    <Typography variant="body2" fontWeight={n.is_read ? 400 : 700} noWrap>
                      {n.subject}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {n.event_label}
                    </Typography>
                  </MenuItem>
                ))}
                <Box sx={{ px: 3, py: 1.5 }}>
                  <Button fullWidth size="small" variant="outlined"
                    onClick={() => { handleNotificationsClose(); navigate('/notifications'); }}>
                    Ver todas
                  </Button>
                </Box>
              </Box>
            )}
          </Menu>

          <Menu
            anchorEl={languageAnchor}
            open={Boolean(languageAnchor)}
            onClose={handleLanguageClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 220,
                borderRadius: 2,
                boxShadow: theme.shadows[8],
              }
            }}
          >
            {availableLanguages.map((language) => {
              const selected = (user?.language || currentLanguage) === language.code;

              return (
                <MenuItem
                  key={language.code}
                  selected={selected}
                  onClick={() => handleLanguageSelect(language.code)}
                  disabled={isChangingLanguage}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography component="span" sx={{ fontSize: '1rem', lineHeight: 1 }}>
                      {language.flag}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: selected ? 700 : 500 }}>
                      {language.label}
                    </Typography>
                  </Box>
                </MenuItem>
              );
            })}
          </Menu>
        </Toolbar>
      
      {/* Desplegable de tareas pendientes */}
      <Menu
        anchorEl={tasksAnchor}
        open={Boolean(tasksAnchor)}
        onClose={handleTasksClose}
        PaperProps={{ sx: { width: 340, maxWidth: '92vw' } }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            {t('hardcoded.components_common_sidebar.tareas_aps')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {pendingTasks.total} pendiente(s)
            {pendingTasks.overdue ? ` · ${pendingTasks.overdue} vencida(s)` : ''}
            {pendingTasks.due_today ? ` · ${pendingTasks.due_today} para hoy` : ''}
          </Typography>
        </Box>
        <Divider />
        {(pendingTasks.tasks || []).length === 0 ? (
          <Box sx={{ px: 2, py: 2 }}>
            <Typography variant="body2" color="text.secondary">
              No tienes tareas pendientes.
            </Typography>
          </Box>
        ) : (
          (pendingTasks.tasks || []).map((task) => (
            <MenuItem
              key={task.id}
              onClick={() => { handleTasksClose(); navigate(`/tasks/${task.id}`); }}
              sx={{ display: 'block', py: 1 }}
            >
              <Typography variant="body2" fontWeight={600} noWrap>{task.title}</Typography>
              <Typography variant="caption" color={task.is_overdue ? 'error.main' : 'text.secondary'}>
                {task.due_at
                  ? `${task.is_overdue ? 'Vencida' : 'Vence'}: ${new Date(task.due_at).toLocaleString()}`
                  : 'Sin fecha límite'}
              </Typography>
            </MenuItem>
          ))
        )}
        <Divider />
        <MenuItem onClick={() => { handleTasksClose(); navigate('/tasks'); }}>
          <Typography variant="body2" color="primary.main" fontWeight={600}>
            Ver todas mis tareas
          </Typography>
        </MenuItem>
      </Menu>

</AppBar>

      {/* Enhanced styles */}
      <style jsx>{`
        @keyframes liveIndicatorPulse {
          0% { opacity: 1; }
          50% { opacity: 0.5; }
          100% { opacity: 1; }
        }
        
        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.1); }
          100% { transform: scale(1); }
        }
      `}</style>
    </>
  );
};

export default Navbar;